<template>
  <layout-main>
    <div class="container">
      <div class="content">
        <md-field md-clearable :class="{'md-invalid': loginIdInvalid}">
          <label>ログインID</label>
          <md-input v-model="loginId" required :disabled="isEditMode" maxlength="50"></md-input>
          <span class="md-error">{{ loginIdInvalidMessage }}</span>
          <md-tooltip v-if="isEditMode" md-direction="left">ログインIDは変更できません</md-tooltip>
        </md-field>

        <md-field md-clearable :class="{'md-invalid': nameInvalid}">
          <label>氏名</label>
          <md-input v-model="name" required maxlength="50"></md-input>
          <span class="md-error">入力してください</span>
        </md-field>

        <md-field md-clearable>
          <label>所属</label>
          <md-input v-model="organization" maxlength="50"></md-input>
        </md-field>

        <md-field md-clearable :class="{'md-invalid': emailInvalid}">
          <label>メールアドレス</label>
          <!-- http://www.rfc-editor.org/errata_search.php?rfc=3696&eid=1690 -->
          <md-input v-model="email" maxlength="254" required></md-input>
          <span class="md-error">入力してください</span>
        </md-field>

        <date-picker label="契約開始日" :value.sync="validFrom" required />

        <date-picker label="契約終了日" :value.sync="validUntil" required />

        <md-field md-clearable>
          <label>備考</label>
          <md-input v-model="note" maxlength="250"></md-input>
        </md-field>

        <!-- ===================================================== -->
        <h2>権限</h2>
        <!-- ===================================================== -->
        <div v-for="r in master.roles" :key="r.code">
          <md-radio
            :class="{'md-primary': r.code !== 'admin'}"
            :value="r.code"
            v-model="role"
          >{{ r.name }}
            <span v-if="r.code === 'admin'">
              <span>&nbsp;</span>
              <md-tooltip :md-active="role === 'admin'" md-direction="right">{{r.name}}はユーザー登録/削除などの全権限を持ちます！ご注意ください。</md-tooltip>
            </span>
          </md-radio>
        </div>

        <!-- ===================================================== -->
        <h2 :class="{ error: companiesInvalid }">参照可能企業</h2>
        <!-- ===================================================== -->
        <p>
          このユーザーが閲覧できる企業を選択してください。
        </p>

        <div class="error" v-if="companiesInvalid">ホールディングスユーザーでない場合、1つ以上の企業を選択してください</div>

        <md-switch v-model="isHoldings" class="md-primary">
          ホールディングス
          <span>
            <md-icon>live_help</md-icon>
            <md-tooltip md-direction="right">ホールディングスユーザーは自動的に全企業参照可能です</md-tooltip>
          </span>
        </md-switch>

        <div v-for="company in master.companies" :key="company.code">
          <md-checkbox
            class="md-primary"
            :value="company.code"
            v-model="companies"
          >{{ company.name }}</md-checkbox>
        </div>

        <!-- ===================================================== -->
        <h2>公開サイト</h2>
        <!-- ===================================================== -->
        <p>
          このユーザーに対して開示するサイトの選択、設定を行います。
        </p>
        <p v-if="noncontractedDataTypes.length > 0">
          <gl-sub-text>
            {{noncontractedDataTypes.join('、')}}サイトの開示をご希望の場合は別途ご相談ください。
          </gl-sub-text>
        </p>

        <md-tabs :md-active-tab="activeTab" @md-changed="activeTab = $event" md-alignment="fixed">
          <template slot="md-tab" slot-scope="{tab}">
            <span
              class="data-type-label"
              :class="{
                error: (tab.data.dataType === 'ID-POS' && hasIdPosErrors) ||
                       (tab.data.dataType === 'POS' && hasPosErrors),
                disabled: tab.data.disabled
              }"
            >
              {{tab.label}}
            </span>
          </template>

          <md-tab
            id="ID-POS"
            :md-template-data="{dataType: 'ID-POS', disabled: !master.contractDataTypes.includes('ID-POS')}"
            :md-disabled="!master.contractDataTypes.includes('ID-POS')"
            md-label="ID-POS開示サイト">

            <!-- =============================-->
            <h3>利用サービス</h3>
            <!-- =============================-->
            <md-checkbox
              v-for="service in idposServices" :key="service.serviceCode"
              class="md-primary"
              :value="service.serviceCode"
              v-model="services"
            >
              {{ service.serviceName }}
            </md-checkbox>

            <div class="mouse" v-if="useMouse">
              <!-- =============================-->
              <h3>Mouse設定</h3>
              <!-- =============================-->
              <mouse
                :companies="mouse.companies"
                :category.sync="mouseCategory"
                :data-types.sync="mouse.dataTypes"
                :active-tab.sync="mouse.activeTab"
                :data-types-invalid="mouse.dataTypesInvalid"
                :categories-invalid="mouse.categoriesInvalid"
              />
            </div>
          </md-tab>
  
          <md-tab
            id="POS"
            :md-template-data="{dataType: 'POS', disabled: !master.contractDataTypes.includes('POS')}"
            :md-disabled="!master.contractDataTypes.includes('POS')"
            md-label="POS開示サイト">

            <!-- =============================-->
            <h3>利用サービス</h3>
            <!-- =============================-->
            <md-checkbox
              v-for="service in posServices" :key="service.serviceCode"
              class="md-primary"
              :value="service.serviceCode"
              v-model="services"
            >
              {{ service.serviceName }}
            </md-checkbox>

            <!-- =============================-->
            <h3>自社商品</h3>
            <!-- =============================-->
            <p>
              このユーザーの「自社商品」と判断するメーカー / 取引先を設定します。「自社商品」を設定すると、分析結果の自社商品のハイライトや開示制限を行うことができます。
            </p>
            <!-- メーカーと取引先の指定をラジオで切り替えるUI -->
            <div>
              <md-radio
                class="md-primary"
                value="none"
                v-model="inHouse.type"
              >
                設定しない
              </md-radio>
              <md-radio
                class="md-primary"
                value="maker"
                v-model="inHouse.type"
              >
                メーカー<gl-sub-text>を設定する</gl-sub-text>
              </md-radio>
              <md-radio
                class="md-primary"
                value="supplier"
                v-model="inHouse.type"
              >
                取引先<gl-sub-text>を設定する</gl-sub-text>
              </md-radio>
              <div v-if="inHouse.type === 'supplier'">
                <supplier :suppliers.sync="inHouse.suppliers" :errors.sync="supplierErrors"/>
                <div class="error" v-if="suppliersInvalid">{{this.supplierErrors[0]}}</div>
              </div>
              <div v-if="inHouse.type === 'maker'">
                <maker :makers.sync="inHouse.makers" :errors.sync="makerErrors"/>
                <div class="error" v-if="makersInvalid">{{this.makerErrors[0]}}</div>
              </div>
            </div>

            <!-- メーカー、取引先どちらも個別に設定できるUI -->
            <!-- <div>
              <md-checkbox
                class="md-primary"
                value="maker"
                v-model="inHouse.enabled"
              >
                メーカー<gl-sub-text>を設定する</gl-sub-text>
              </md-checkbox>
              <div class="inhouse-sub-box" v-if="inHouse.enabled.includes('maker')">
                <maker :makers.sync="inHouse.makers" :errors.sync="makerErrors"/>
                <div class="error" v-if="makersInvalid">{{this.makerErrors[0]}}</div>
              </div>
            </div>
            <div>
              <md-checkbox
                class="md-primary"
                value="supplier"
                v-model="inHouse.enabled"
              >
                取引先<gl-sub-text>を設定する</gl-sub-text>
              </md-checkbox>
              <div class="inhouse-sub-box" v-if="inHouse.enabled.includes('supplier')">
                <supplier :suppliers.sync="inHouse.suppliers" :errors.sync="supplierErrors"/>
                <div class="error" v-if="suppliersInvalid">{{this.supplierErrors[0]}}</div>
              </div>
            </div> -->

            <!-- =============================-->
            <h3>POS閲覧可能分類</h3>
            <!-- =============================-->
            <div>
              <div :hidden="master.companies.length === 1">
                <md-radio v-for="c in activeCompanies" :value="c.code" v-model="posCategorySelectingCompany"
                  :key="c.code">
                  {{ c.name }}
                </md-radio>
              </div>
              <p v-if="activeCompanies.length === 0" class="caution">
                閲覧可能分類を設定するには、まずこのユーザーが参照可能な企業を「参照可能企業」で設定してください。
              </p>
              <category
                v-if="editingPosCategories"
                :company="editingPosCategories.companyCode"
                v-bind.sync="editingPosCategories"
              />
              <p
                class="error"
                v-if="editingPosCategories && posCategoriesErrors[editingPosCategories.companyCode]"
              >
                {{posCategoriesErrors[editingPosCategories.companyCode]}}
              </p>
              <p>
                <md-checkbox class="md-primary" v-model="posRestrictToInhouse">
                  POS分析で閲覧可能商品は自社商品のみ
                </md-checkbox>
              </p>
            </div>
          </md-tab>
        </md-tabs>

        <div class="actions">
          <div>
            <router-link to="/users">
              <md-button>
                <font-awesome-icon icon="arrow-left"/>リストに戻る
              </md-button>
            </router-link>
          </div>
          <div class="actions-align-end">
            <md-button class="md-raised md-primary" @click="save">Save</md-button>
          </div>
        </div>
      </div>

      <md-snackbar
        md-position="center"
        :md-duration="snackbarDuration"
        :md-active.sync="showSnackbar"
        md-persistent
        @md-closed="backToList"
      >
        <span>{{ message }}</span>
      </md-snackbar>
    </div>
  </layout-main>
</template>

<script>
import LayoutMain from '@/components/layout/LayoutMain'
import DatePicker from '@/components/DatePicker'
import moment from 'moment'
import Maker from '@/components/Maker.vue'
import Supplier from '@/components/Supplier.vue'
import Mouse from '@/components/Mouse.vue'
import Category from '@/components/Category.vue'

const LOGIN_ID_CHARS_ALLOWED = new RegExp('[^a-zA-Z0-9\\-\\_]')
const INITIAL_START = moment().format('YYYY-MM-DD')
const INITIAL_END = moment()
  .add(1, 'years')
  .format('YYYY-MM-DD')
const DATA_TYPES = ['ID-POS', 'POS']
// POS閲覧可能分類の最大要素数 (企業毎)
const USER_POS_CATEGORIES_MAX_LEN = 100

export default {
  components: { LayoutMain, DatePicker, Maker, Supplier, Mouse, Category },
  name: 'User',
  data() {
    return {
      mode: !this.$route.params.loginId ? 'ADD' : 'EDIT',
      loginId: '',
      userId: '',
      name: '',
      organization: '',
      role: undefined,
      email: '',
      validFrom: INITIAL_START,
      validUntil: INITIAL_END,
      note: '',
      companies: [],
      services: [],
      makers: [],
      message: undefined,
      showSnackbar: false,
      snackbarDuration: 1500,
      loginIdInvalid: false,
      loginIdInvalidMessage: '',
      nameInvalid: false,
      emailInvalid: false,
      validFromInvalid: false,
      validUntilInvalid: false,
      companiesInvalid: false,
      makersInvalid: false,
      suppliersInvalid: false,
      // POS閲覧可能分類エラー ※キーは企業
      posCategoriesErrors: {},
      makerErrors: [],
      supplierErrors: [],
      // 自社商品
      inHouse: {
        type: 'none', // 自社商品選択をラジオにする場合にはこれ
        // enabled: [], // 自社商品選択をチェックボックスにする場合はこれ
        makers: [],
        suppliers: [],
      },
      master: {
        allDataTypes: DATA_TYPES,
        roles: [],
        companies: [],
        services: [],
        contractDataTypes: [],
      },
      mouse: {
        accounts: [],
        dataTypes: undefined,
        activeTab: undefined,
        dataTypesInvalid: false,
        categoriesInvalid: false,
        companies: [],
      },
      activeTab: undefined,
      // POS閲覧可能分類
      posCategorySelectingCompany: undefined,
      posRestrictToInhouse: false,
      posCategoriesPerCompany: [],
      // mouseAccounts: [],
      // mouseDataTypes: undefined,
      // mouseActiveTab: undefined,
      // mouseDataTypesInvalid: false,
      // mouseCategoriesInvalid: false,
      // mouseCompanies: [],
    }
  },
  watch: {
    companies(newVal) {
      //ホールディングはnull
      if (newVal === null) {
        this.mouse.companies = this.master.companies
      } else {
        const companies = this.master.companies.filter(
          company => newVal.indexOf(company.code) >= 0,
        )
        this.mouse.companies = companies
      }

      if (this.mouse.companies.length > 0) {
        this.mouse.activeTab = this.mouse.companies[0].code
      }
    },
    activeCompanies(companies) {
      if (companies.length > 0) {
        this.posCategorySelectingCompany = companies[0].code
      } else {
        this.posCategorySelectingCompany = undefined
      }
    },
  },
  computed: {
    mouseCategory() {
      /**
       * Mouseで使用できるカテゴリを取得する
       */
      const category = this.mouse.accounts.find(
        c => c.companyCode === this.mouse.activeTab,
      )

      if (!category) {
        return {
          categoryLevel: 1,
          categorySelect: 'all',
          categories: [],
        }
      } else {
        return category
      }
    },
    useMouse() {
      //直書きスマソ
      return this.services.includes('mouse')
    },
    isAddMode() {
      return this.mode === 'ADD'
    },
    isEditMode() {
      return this.mode === 'EDIT'
    },
    isHoldings: {
      get() {
        return this.companies === null
      },
      set(v) {
        if (v) {
          this.companies = null
        } else {
          this.companies = this.master.companies
            .filter(c => c.visible)
            .map(c => c.code)
        }
      },
    },
    idposServices() {
      return this.master.services.filter(s => s.dataType === 'ID-POS')
    },
    posServices() {
      return this.master.services.filter(s => s.dataType === 'POS')
    },
    noncontractedDataTypes() {
      return DATA_TYPES.filter(t => !this.master.contractDataTypes.includes(t))
    },
    activeCompanies() {
      return this.isHoldings
        ? this.master.companies
        : this.master.companies.filter(c => this.companies.includes(c.code))
    },
    editingPosCategories() {
      return this.posCategoriesPerCompany.find(
        c => c.companyCode === this.posCategorySelectingCompany,
      )
    },
    // ID-POS開示サイトで入力不正がある
    hasIdPosErrors() {
      return false
    },
    // POS開示サイトで入力不正がある
    // （スマートではない…）
    hasPosErrors() {
      if (Object.keys(this.posCategoriesErrors).length > 0) {
        return true
      }
      return false
    },
  },
  methods: {
    serviceClick(id) {
      if (this.services.includes(id)) {
        this.services = this.services.filter(s => s !== id)
      } else {
        this.services = [...this.services, id]
      }
    },
    async save() {
      const inHouse = {
        makers:
          this.inHouse.type === 'maker'
            ? this.inHouse.makers.map(i =>
                [i.companyCode, i.makerCode].join(','),
              )
            : [],
        suppliers:
          this.inHouse.type === 'supplier'
            ? this.inHouse.suppliers.map(i =>
                [i.companyCode, i.supplierCode].join(','),
              )
            : [],
      }

      const userProps = {
        userId: this.userId,
        loginId: this.loginId,
        name: this.name,
        organization: this.organization,
        roles: [this.role],
        companies: this.companies,
        email: this.email,
        note: this.note,
        validThrough: [
          moment(this.validFrom, 'YYYY-MM-DD').format('YYYY-MM-DD'),
          moment(this.validUntil, 'YYYY-MM-DD').format('YYYY-MM-DD'),
        ],
        services: this.services,
        inHouse,
        posRestrictToInhouse: this.posRestrictToInhouse,
      }

      // POS閲覧可能分類
      if (this.posServices.some(s => this.services.includes(s.serviceCode))) {
        userProps['posCategories'] = this.posCategoriesPerCompany
          .filter(
            c =>
              this.companies === null ||
              (this.companies || []).includes(c.companyCode),
          )
          .filter(c => c.categories && c.categories.length > 0)
          .map(c => ({ ...c }))
      } else {
        userProps['posCategories'] = []
      }

      let mouseProps = []
      let companies
      if (userProps.services.includes('mouse')) {
        if (this.companies === null) {
          companies = this.master.companies
        } else {
          companies = this.master.companies.filter(
            company => this.companies.indexOf(company.code) >= 0,
          )
        }

        for (const { code } of companies) {
          const findMouseAccount = this.mouse.accounts.find(
            m => m.companyCode === code,
          )

          mouseProps.push({
            userId: this.userId,
            companyCode: code,
            dataTypes: this.mouse.dataTypes,
            categoryLevel:
              findMouseAccount.categorySelect === 'all '
                ? null
                : findMouseAccount.categoryLevel,
            categories: findMouseAccount.categories,
          })
        }
      }

      if (!this.validate(userProps) || !this.mouseValidate(mouseProps)) {
        return
      }

      if (this.isAddMode) {
        const err = await this.$account.addAccount(
          this.$store.state.user.companyCode,
          userProps,
        )

        if (err) {
          alert(err.message)
          return
        }

        if (mouseProps.length > 0) {
          //追加されたユーザIDを取得する
          const user = await this.$account.getAccount(
            this.$store.state.user.companyCode,
            this.loginId,
          )
          const userId = user.userId
          mouseProps.map(prop => (prop.userId = userId))

          const mouseErr = await this.$mouseAccount.add(mouseProps)
          if (mouseErr) {
            alert(mouseErr.message)
            return
          }
        }

        this.message = '追加しました'
      } else {
        await this.$account.updateAccount(
          this.$store.state.user.companyCode,
          this.loginId,
          userProps,
        )

        if (mouseProps.length > 0) {
          const mouseErr = await this.$mouseAccount.add(mouseProps)
          if (mouseErr) {
            alert(mouseErr.message)
            return
          }
        }

        this.message = '変更しました'
      }

      this.showSnackbar = true
    },
    validate(props) {
      let hasError = false

      if (!props.loginId || props.loginId === '') {
        this.loginIdInvalid = true
        this.loginIdInvalidMessage = '入力してください'
        hasError = true
      } else if (LOGIN_ID_CHARS_ALLOWED.test(props.loginId)) {
        this.loginIdInvalid = true
        this.loginIdInvalidMessage =
          '英数字と一部の記号（「-」「_」）のみ使用可能です'
        hasError = true
      } else {
        this.loginIdInvalid = false
        this.loginIdInvalidMessage = ''
      }

      if (!props.name || props.name === '') {
        this.nameInvalid = true
        hasError = true
      } else {
        this.nameInvalid = false
      }

      if (!props.email || props.email === '') {
        this.emailInvalid = true
        hasError = true
      } else {
        this.emailInvalid = false
      }

      const validFrom = props.validThrough[0]
      const validUntil = props.validThrough[1]

      // TODO 正規表現とかでチェックしろ！
      if (!validFrom || validFrom === '' || validFrom.length !== 10) {
        this.validFromInvalid = true
        hasError = true
      } else {
        this.validFromInvalid = false
      }

      if (!validUntil || validUntil === '' || validUntil.length !== 10) {
        this.validUntilInvalid = true
        hasError = true
      } else {
        this.validUntilInvalid = false
      }

      if (this.companies !== null && this.companies.length === 0) {
        this.companiesInvalid = true
        hasError = true
      } else {
        this.companiesInvalid = false
      }

      // 自社商品
      // TODO 汚い
      if (this.inHouse.type === 'maker') {
        if (this.makerErrors.length) {
          // メーカー選択コンポーネント側で既にエラー（上限超過）
          this.makersInvalid = true
          hasError = true
        } else if (this.inHouse.makers.length === 0) {
          this.makerErrors = ['メーカーを選択してください。']
          this.makersInvalid = true
          hasError = true
        } else {
          this.makersInvalid = false
        }
      } else if (this.inHouse.type === 'supplier') {
        if (this.supplierErrors.length) {
          this.suppliersInvalid = true
          hasError = true
        } else if (this.inHouse.suppliers.length === 0) {
          this.supplierErrors = ['取引先を選択してください。']
          this.suppliersInvalid = true
          hasError = true
        } else {
          this.suppliersInvalid = false
        }
      } else {
        this.makersInvalid = false
        this.suppliersInvalid = false
      }
      // POS閲覧可能分類 (企業毎検査)
      for (const cate of props.posCategories || []) {
        const posCategoriesErrors = {}
        if (!cate.categories) {
          // NOP
        } else if (cate.categories.length > USER_POS_CATEGORIES_MAX_LEN) {
          posCategoriesErrors[cate.companyCode] =
            `${USER_POS_CATEGORIES_MAX_LEN}分類以上は指定できません` +
            `（現在の分類数: ${cate.categories.length}）。` +
            'ヒント: 上位階層で指定する'
          hasError = true
        }
        this.posCategoriesErrors = posCategoriesErrors
      }

      return !hasError
    },
    mouseValidate(props) {
      if (props === undefined || props.length === 0) {
        return true
      }

      let hasError = false
      this.mouse.categoriesInvalid = false
      this.mouse.dataTypesInvalid = false

      for (const prop of props) {
        if (prop.dataTypes.length === 0) {
          this.mouse.dataTypesInvalid = true
          hasError = true
        }

        if (prop.categorySelect === 'select' && prop.categories.length === 0) {
          this.mouse.categoriesInvalid = true
          hasError = true
          break
        }
      }

      return !hasError
    },
    backToList() {
      this.$router.push('/users')
    },
  },
  async created() {
    // TODO 頻繁に変わるものではないし、ページ開く度にリクエストする必要はなさそうだが、、、
    this.master.roles = await this.$role.getRoles()
    this.master.services = await this.$service.getServices()
    this.master.companies = await this.$company.getCompanies()
    console.log('companies ->', this.master.companies)
    const companyCode = this.$store.state.user.companyCode
    console.log('state user company', companyCode)
    const site = await this.$site.getSite()
    this.master.contractDataTypes = site.contractDataTypes
    // ID-POS/POSは少なくとも一つは有効のはず
    this.activeTab = DATA_TYPES.find(t =>
      this.master.contractDataTypes.includes(t),
    )
    // ユーザーの契約企業コード以外を非表示にしていましたが、
    // マルチテナント版では契約企業コードは廃止されたため全企業参照可能です。
    // this.master.companies.map(
    //   company => (company.visible = company.code === companyCode),
    // )

    if (this.isAddMode) {
      this.userId = ''
      this.loginId = ''
      this.role = this.master.roles[this.master.roles.length - 1].code
      this.companies = this.master.companies.map(c => c.code)
    } else {
      const user = await this.$account.getAccount(
        this.$store.state.user.companyCode,
        this.$route.params.loginId,
      )
      if (!user) {
        this.$router.push('/usernotfound')
        return
      }
      this.userId = user.userId
      this.loginId = user.loginId
      this.name = user.name
      this.organization = user.organization
      this.role = user.roles[0]
      this.companies = user.companies
      this.email = user.email
      this.validFrom = moment(user.validThrough[0], 'YYYYMMDD').format(
        'YYYY-MM-DD',
      )
      this.validUntil = moment(user.validThrough[1], 'YYYYMMDD').format(
        'YYYY-MM-DD',
      )
      this.note = user.note
      this.services = user.services

      if (user.inHouse) {
        if (user.inHouse.makers !== null && 0 < user.inHouse.makers.length) {
          this.inHouse.makers = await this.$maker.getByCode(
            user.inHouse.makers.map(i => i.replace(',', ' ')),
          )
          this.inHouse.type = 'maker'
        }
        if (
          user.inHouse.suppliers !== null &&
          0 < user.inHouse.suppliers.length
        ) {
          this.inHouse.suppliers = await this.$supplier.getByCode(
            user.inHouse.suppliers.map(i => i.replace(',', ' ')),
          )
          this.inHouse.type = 'supplier'
        }
      }

      this.posRestrictToInhouse = user.posRestrictToInhouse
    }

    // POS閲覧可能分類
    const userPosCategories = this.userId
      ? await this.$account.getUserPosCategories(this.userId)
      : []

    console.log('pos categories', userPosCategories)

    this.posCategoriesPerCompany = await Promise.all(
      this.master.companies.map(async ({ code }) => {
        const levels = await this.$itemCategoryLevels.get(code)

        const existingSetting = userPosCategories.find(
          uc => uc.companyCode === code,
        )

        if (!existingSetting) {
          return {
            companyCode: code,
            categorySelect: 'all',
            categoryLevel: levels[0].level, // 最上位階層
            categories: [],
          }
        } else {
          const all = !existingSetting.categoryLevel
          return {
            companyCode: code,
            categorySelect: all ? 'all' : 'select',
            categoryLevel: all
              ? levels[0].level
              : existingSetting.categoryLevel,
            categories: all ? [] : existingSetting.categories,
          }
        }
      }),
    )

    //Mouse
    let accounts = []
    if (this.userId && this.useMouse) {
      accounts = await this.$mouseAccount.get(this.userId)
    }

    //データ種別はどの企業も同じ
    //とりあえず1つ目を取得しておく
    if (accounts.length > 0) {
      this.mouse.dataTypes = accounts[0].dataTypes
    } else {
      this.mouse.dataTypes = ['pos', 'idpos']
    }

    for (const { code } of this.master.companies) {
      let account = accounts.find(c => c.companyCode === code)
      if (!account) {
        account = {
          companyCode: code,
          categories: [],
          categoryLevel: 1,
        }
      }
      account.categorySelect =
        account.categories === null || account.categories.length === 0
          ? 'all'
          : 'select'

      this.mouse.accounts.push(account)
    }

    if (this.companies === null || this.companies.length === 0) {
      this.mouse.activeTab = this.master.companies[0].code
    } else {
      const companies = this.master.companies.filter(
        company => this.companies.indexOf(company.code) >= 0,
      )
      this.mouse.activeTab = companies[0].code
    }
  },
}
</script>

<style lang="scss" scoped>
h2,
h3 {
  padding-top: var(--spacing-large);
}

.container {
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.content {
  width: 600px;
  margin-top: 24px;
  margin-bottom: 24px;
}

.actions {
  border-top: 1px solid #cfd8dc;
  display: flex;
  margin-top: 24px;
  padding-top: 24px;

  .actions-align-end {
    flex: 1;
    text-align: right;
  }
}

.data-type-label.disabled {
  text-decoration: line-through;
}

.inhouse-sub-box {
  padding-left: var(--spacing-medium);
  margin-left: var(--spacing-medium);
  border-left: 3px solid var(--color-border);
}
</style>
